import { createSelector } from '@reduxjs/toolkit';

import { REWARD_BUNDLE_TYPES, REWARD_STATUSES, REWARD_STATUSES_CLAIMABLE, REWARD_STATUSES_CLAIMED } from '~/constants';

import type { RootState } from '~/store';

export const patrolsEndDateSelector = (state: RootState) => state.patrols.eventFinishesAt;
export const patrolsNextUpdateSelector = (state: RootState) => state.patrols.nextUpdateAt;
export const patrolsBonusTypeSelector = (state: RootState) => state.patrols.bonusType;
export const patrolsSuperBundleChanceStepSelector = (state: RootState) => state.patrols.superBundleChanceStep;
export const patrolsSettingsSelector = (state: RootState) => state.patrols.patrols;
export const patrolsBundlesSelector = (state: RootState) => state.patrols.bundles;
export const patrolsRewardsSelector = (state: RootState) => state.patrols.rewards;

export const patrolsMembersSelector = (state: RootState) => state.patrols.members.result;
export const patrolsAccountSelector = (state: RootState) => state.patrols.account.result;

// Account Info
export const patrolsAccountInfoSelector = createSelector(patrolsAccountSelector, (account) => account?.accountInfo);

export const patrolsAccountSuperBundleChanceSelector = createSelector(
  patrolsAccountInfoSelector,
  (accountInfo) => accountInfo?.superBundleChance || 0,
);

export const patrolsAccountBonusIsPendingSelector = createSelector(
  patrolsAccountSelector,
  (account) => account?.bonusClaimInProgress || false,
);

// Active Patrol
export const patrolsActivePatrolSelector = createSelector(
  patrolsSettingsSelector,
  patrolsAccountInfoSelector,
  (patrols, accountInfo) => {
    return patrols?.find((e) => e.id === accountInfo?.patrolId);
  },
);

// Bundle and Rewards
const patrolsCurrentBundleSelector = createSelector(patrolsAccountSelector, (account) => account?.currentBundle);
export const patrolsCurrentRewardsSelector = createSelector(
  patrolsCurrentBundleSelector,
  (currentBundle) => currentBundle?.rewards || [],
);
export const patrolsCurrentBundleConfigSelector = createSelector(
  patrolsBundlesSelector,
  patrolsCurrentBundleSelector,
  (bundles, currentBundle) => {
    if (!currentBundle?.id) {
      return null;
    }
    return bundles[currentBundle.id];
  },
);

// Rewards by Status
export const patrolsProgressRewardsSelector = createSelector(patrolsCurrentRewardsSelector, (currentRewards) =>
  currentRewards.filter((reward) => REWARD_STATUSES_CLAIMED.includes(reward.status)),
);

const patrolsClaimableRewardsSelector = createSelector(patrolsCurrentRewardsSelector, (currentRewards) =>
  currentRewards.filter((reward) => REWARD_STATUSES_CLAIMABLE.includes(reward.status)),
);
export const patrolsHasClaimableRewardsSelector = createSelector(
  patrolsClaimableRewardsSelector,
  (rewards) => rewards.length > 0,
);

const patrolsPendingRewardsSelector = createSelector(patrolsCurrentRewardsSelector, (currentRewards) =>
  currentRewards.filter((reward) => reward.status === REWARD_STATUSES.PENDING),
);
export const patrolsHasPendingRewardsSelector = createSelector(
  patrolsPendingRewardsSelector,
  (pendingRewards) => pendingRewards.length > 0,
);

const patrolsNewRewardsSelector = createSelector(patrolsCurrentRewardsSelector, (currentRewards) =>
  currentRewards.filter((reward) => reward.status === REWARD_STATUSES.NEW),
);
export const patrolsHasNewRewardsSelector = createSelector(patrolsNewRewardsSelector, (rewards) => rewards.length > 0);

export const patrolsHasAllNewRewardsSelector = createSelector(patrolsCurrentRewardsSelector, (currentRewards) =>
  !currentRewards.some((reward) => reward.status !== REWARD_STATUSES.NEW),
);

// Bundles
const patrolsClaimedBundlesSelector = createSelector(
  patrolsAccountSelector,
  (account) => account?.claimedBundles || [],
);
const patrolsSuperBundleIdsSelector = createSelector(patrolsBundlesSelector, (bundles) =>
  Object.values(bundles)
    .filter((bundle) => bundle.type === REWARD_BUNDLE_TYPES.SUPER)
    .map((bundle) => bundle.id),
);
export const patrolsUnclaimedRewardsAmountSelector = createSelector(
  patrolsClaimedBundlesSelector,
  patrolsSuperBundleIdsSelector,
  (claimedBundles, superBundleIds) => {
    const missingBundleIds = superBundleIds.filter((bundleId) => !claimedBundles.includes(bundleId));
    return missingBundleIds.length;
  },
);
